import React, { FunctionComponent } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import { pathOr } from 'ramda';
import { Container } from 'semantic-ui-react';
import queryString from 'query-string';

import Query from 'components/Query';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';
import { ClassProp } from 'helpers/types';
import { ChartPlaceholder } from 'components/Placeholder';
import {
  UPSERT_BRAND_ASSETS,
  GET_BRAND_ASSETS,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.queries';
import {
  Merchant,
  BrandAsset,
  BrandBundleType,
  AssetUpsertResponse,
  Values,
  getApolloError,
  defaultAssetUpsertSuccessMessage,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.helpers';
import styles from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.styles';

import DefaultAssetsUpdate from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/components/DefaultAssetsUpdate/DefaultAssetsUpdate';
import VenmoAssetsUpdate from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/components/VenmoAssetsUpdate/VenmoAssetsUpdate';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  merchant: Merchant;
  classes: Styles;
};

type QueryResponse = {
  loading: boolean;
  data: {
    brand: BrandAsset;
  };
};

const renderAssets = (
  classes: Styles,
  merchant: Merchant,
  brandBundle: string,
  doAssetUpsert: Function,
  isSubmitting: boolean,
) => (response: QueryResponse) => {
  if (response.loading) {
    return <ChartPlaceholder className={classes.loading} />;
  }

  const assets = pathOr({}, ['data', 'brand', 'assets'], response);

  return (
    <Container className={classes.container}>
      {brandBundle === BrandBundleType.DEFAULTBRANDING && (
        <DefaultAssetsUpdate
          brandAssets={assets}
          merchant={merchant}
          upsertBrandAssets={doAssetUpsert}
          isSubmitting={isSubmitting}
        />
      )}
      {brandBundle === BrandBundleType.VENMOAPP && (
        <VenmoAssetsUpdate
          brandAssets={assets}
          merchant={merchant}
          upsertBrandAssets={doAssetUpsert}
          isSubmitting={isSubmitting}
        />
      )}
    </Container>
  );
};

const BrandAssetManagement: FunctionComponent<Props> = ({
  classes,
  merchant,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { merchantId, brandId } = merchant;
  const { brandBundle = '' }: Values = queryString.parse(location.search);
  const assetBrandId = brandId || merchantId.replace(/-/g, '');

  const handleSuccess = (success: any) =>
    toastMapping({}, 'success')(success as any);

  const handleAssetUpsertResponse = (
    assetUpsertResponse: AssetUpsertResponse,
  ) => {
    const success = pathOr(
      false,
      ['upsertBrandAsset', 'success'],
      assetUpsertResponse,
    );

    if (success) {
      handleSuccess(defaultAssetUpsertSuccessMessage);
      history.goBack();
    }
  };

  const doAssetUpsert = (assets: BrandAsset) =>
    upsertAssets({
      variables: {
        assets,
        brandId: assetBrandId,
        bundle: brandBundle,
      },
    });

  const handleError = (error: any) => {
    toastMapping({}, 'error')(getApolloError(error) as any);
  };

  const [upsertAssets, { loading: isSubmitting }] = useMutation(
    UPSERT_BRAND_ASSETS,
    {
      onCompleted: handleAssetUpsertResponse,
      onError: handleError,
    },
  );

  return (
    <Query
      enableProgressInfo
      query={GET_BRAND_ASSETS}
      render={renderAssets(
        classes,
        merchant,
        brandBundle,
        doAssetUpsert,
        isSubmitting,
      )}
      variables={{
        id: assetBrandId,
        version: 'LATEST',
        bundle: brandBundle,
      }}
    />
  );
};

export default withStyles(styles)(BrandAssetManagement);
